import { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PiArrowLeftBold, PiLockKeyBold } from 'react-icons/pi';
import toast from 'react-hot-toast';
import { Button, Title, Text } from '@v2/design-system';
import api from '@v2/lib/api';
import { useI18n } from '@v2/providers/I18nProvider';
import PloomesDealDetailView from '@v2/features/ploomes-deals/detail/PloomesDealDetailView';
import { ploomesDealPath } from '@v2/features/ploomes-deals/dealPath';
import type { PloomesDealDetail } from '@v2/features/ploomes-deals/types';
import { dealRelationsQueryKey } from '@v2/features/ploomes-deals/detail/useDealRelations';

function SkeletonLine({ width }: { width: string }) {
    return <div className={`h-3 rounded-md bg-gray-200 dark:bg-zinc-700 ${width}`} />;
}

function PloomesDealDetailSkeleton() {
    return (
        <div className="animate-pulse space-y-6">
            <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900">
                <div className="mb-4 space-y-2">
                    <SkeletonLine width="w-72" />
                    <SkeletonLine width="w-52" />
                </div>
                <div className="flex flex-wrap gap-2">
                    <div className="h-9 w-40 rounded-lg bg-gray-200 dark:bg-zinc-700" />
                    <div className="h-9 w-36 rounded-lg bg-gray-200 dark:bg-zinc-700" />
                    <div className="h-9 w-44 rounded-lg bg-gray-200 dark:bg-zinc-700" />
                    <div className="h-9 w-36 rounded-lg bg-gray-200 dark:bg-zinc-700" />
                </div>
            </div>

            <div className="grid gap-6 lg:grid-cols-12">
                <div className="space-y-6 lg:col-span-8">
                    <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900">
                        <div className="mb-4 h-5 w-56 rounded bg-gray-200 dark:bg-zinc-700" />
                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                            {Array.from({ length: 10 }).map((_, i) => (
                                <div key={i} className="h-24 rounded-xl bg-gray-100 dark:bg-zinc-800" />
                            ))}
                        </div>
                    </div>
                </div>

                <div className="space-y-6 lg:col-span-4">
                    {Array.from({ length: 5 }).map((_, i) => (
                        <div key={i} className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900">
                            <div className="mb-4 h-5 w-36 rounded bg-gray-200 dark:bg-zinc-700" />
                            <div className="space-y-2">
                                <SkeletonLine width="w-full" />
                                <SkeletonLine width="w-5/6" />
                                <SkeletonLine width="w-4/6" />
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="grid gap-6 lg:grid-cols-3">
                {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-zinc-800 dark:bg-zinc-900">
                        <div className="mb-4 h-5 w-32 rounded bg-gray-200 dark:bg-zinc-700" />
                        <div className="space-y-2">
                            <SkeletonLine width="w-full" />
                            <SkeletonLine width="w-3/4" />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

type DealLoadErrorKind = 'forbidden' | 'not_found' | 'timeout' | 'error';

function DealAccessDenied({
    kind,
    onRetry,
}: {
    kind: DealLoadErrorKind;
    onRetry?: () => void;
}) {
    const { t } = useI18n();

    const titleKey =
        kind === 'forbidden'
            ? 'v2.ploomes_deals.access_denied_title'
            : kind === 'timeout'
              ? 'v2.ploomes_deals.timeout_title'
              : kind === 'error'
                ? 'v2.ploomes_deals.load_error_title'
                : 'v2.ploomes_deals.not_found_title';

    const bodyKey =
        kind === 'forbidden'
            ? 'v2.ploomes_deals.access_denied_body'
            : kind === 'timeout'
              ? 'v2.ploomes_deals.timeout_body'
              : kind === 'error'
                ? 'v2.ploomes_deals.load_error_body'
                : 'v2.ploomes_deals.not_found_body';

    return (
        <div className="mx-auto flex max-w-lg flex-col items-center rounded-2xl border border-gray-200 bg-white px-6 py-12 text-center dark:border-zinc-800 dark:bg-zinc-900">
            <div className="mb-4 flex size-14 items-center justify-center rounded-2xl bg-rose-50 text-rose-600 dark:bg-rose-950/40 dark:text-rose-400">
                <PiLockKeyBold className="size-7" />
            </div>
            <Title as="h1" className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {t(titleKey)}
            </Title>
            <Text className="mt-2 text-sm text-gray-500 dark:text-zinc-400">
                {t(bodyKey)}
            </Text>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
                {(kind === 'timeout' || kind === 'error') && onRetry && (
                    <Button onClick={onRetry}>{t('v2.ploomes_deals.retry')}</Button>
                )}
                <Link to="/ploomes-deals">
                    <Button as="span" variant={kind === 'timeout' || kind === 'error' ? 'outline' : undefined}>
                        <PiArrowLeftBold className="me-1.5 inline size-4" />
                        {t('v2.ploomes_deals.back_to_list')}
                    </Button>
                </Link>
            </div>
        </div>
    );
}

function dealLoadErrorKind(error: unknown): DealLoadErrorKind {
    if (typeof error === 'object' && error !== null) {
        const err = error as {
            code?: string;
            message?: string;
            response?: { status?: number };
        };

        if (err.code === 'ECONNABORTED' || /timeout/i.test(err.message ?? '')) {
            return 'timeout';
        }

        const status = err.response?.status;
        if (status === 403) {
            return 'forbidden';
        }
        if (status === 404) {
            return 'not_found';
        }
        if (status !== undefined) {
            return 'error';
        }
    }

    return 'error';
}

export default function PloomesDealDetailPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const qc = useQueryClient();

    const {
        data: deal,
        isPending,
        isError,
        error,
        isFetching,
        refetch,
    } = useQuery({
        queryKey: ['ploomes-deal', id],
        enabled: Boolean(id),
        retry: false,
        queryFn: async ({ signal }) => {
            // Show enxuto (sem produtos/comissão/tasks). Timeout curto — se passar disso, algo está errado.
            const { data } = await api.get<{ data: PloomesDealDetail }>(`/ploomes-deals/${id}`, {
                signal,
                timeout: 15_000,
            });
            return data.data;
        },
    });

    useEffect(() => {
        if (!deal?.url_key || !id || id === deal.url_key) {
            return;
        }

        navigate(ploomesDealPath(deal), { replace: true });
    }, [deal, id, navigate]);

    const syncMutation = useMutation({
        mutationFn: async () => {
            const { data } = await api.post(`/ploomes-deals/${id}/sync`);
            return data;
        },
        onSuccess: (data) => {
            toast.success(data.message ?? 'Sincronizado');
            qc.invalidateQueries({ queryKey: ['ploomes-deal', id] });
            qc.invalidateQueries({ queryKey: ['ploomes-deal-products', deal?.id ?? id] });
            qc.invalidateQueries({ queryKey: ['ploomes-deal-commission-preview', id] });
            qc.invalidateQueries({ queryKey: ['ploomes-deal-commission-preview', deal?.id] });
            if (deal?.id) {
                qc.invalidateQueries({ queryKey: dealRelationsQueryKey(deal.id) });
            }
            qc.invalidateQueries({ queryKey: ['ploomes-deal-field-sections', deal?.id] });
        },
        onError: () => toast.error('Falha ao sincronizar'),
    });

    const shipmentMutation = useMutation({
        mutationFn: async () => {
            const { data } = await api.post<{ redirect_url: string; message: string; shipment_id?: number }>(
                `/ploomes-deals/${id}/create-shipment`,
            );
            return data;
        },
        onSuccess: (data) => {
            toast.success(data.message);
            if (data.shipment_id) {
                navigate(`/shipments/${data.shipment_id}`);
            }
            qc.invalidateQueries({ queryKey: ['ploomes-deal', id] });
        },
        onError: () => toast.error('Falha ao criar embarque'),
    });

    if (isPending && isFetching) {
        return <PloomesDealDetailSkeleton />;
    }

    if (isError || !deal) {
        return (
            <DealAccessDenied
                kind={isError ? dealLoadErrorKind(error) : 'not_found'}
                onRetry={() => {
                    void refetch();
                }}
            />
        );
    }

    return (
        <PloomesDealDetailView
            deal={deal}
            onSync={() => syncMutation.mutate()}
            syncLoading={syncMutation.isPending}
            onCreateShipment={() => shipmentMutation.mutate()}
            shipmentLoading={shipmentMutation.isPending}
            onTasksRefresh={() => {
                qc.invalidateQueries({ queryKey: ['ploomes-deal', id] });
                qc.invalidateQueries({ queryKey: dealRelationsQueryKey(deal.id) });
            }}
        />
    );
}
